import "./index.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import BottomTabs from "./components/BottomTabs";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Leagues from "./pages/Leagues";
import LeagueDetail from "./pages/LeagueDetail";
import Rankings from "./pages/Rankings";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Feedback from "./pages/Feedback";
import Settings from "./pages/Settings";
import Outstanding from "./pages/Outstanding";
import MatchDetail from "./pages/MatchDetail";
import MyClub from "./pages/MyClub";
import OrganiserClubs from "./pages/OrganiserClubs";
import AdminStats from "./pages/admin/AdminStats";
import AdminInvites from "./pages/admin/AdminInvites";
import AdminClubs from "./pages/admin/AdminClubs";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AuthCallback from "./components/AuthCallback";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#A8E12B" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

const TAB_PATHS = ["/dashboard", "/leagues", "/rankings", "/profile"];

function Shell() {
  const location = useLocation();
  const { user } = useAuth();
  const showTabs = !!user && TAB_PATHS.some((p) => location.pathname === p || (p !== "/dashboard" && location.pathname.startsWith(p + "/")));

  useEffect(() => { document.title = "Vollea — LeaguePoint"; }, []);

  // CRITICAL: Detect the Emergent OAuth fragment BEFORE the rest of the router
  // runs. If session_id is in the URL hash, hand off to AuthCallback so it can
  // exchange it for a token before any Protected route triggers a /auth/me check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth-callback" element={<AuthCallback />} />

        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/leagues" element={<Protected><Leagues /></Protected>} />
        <Route path="/leagues/:lid" element={<Protected><LeagueDetail /></Protected>} />
        <Route path="/rankings" element={<Protected><Rankings /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/profile/edit" element={<Protected><EditProfile /></Protected>} />
        <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/outstanding" element={<Protected roles={["club_admin", "league_organiser", "super_admin"]}><Outstanding /></Protected>} />
        <Route path="/matches/:mid" element={<Protected><MatchDetail /></Protected>} />
        <Route path="/my-club" element={<Protected roles={["club_admin", "super_admin"]}><MyClub /></Protected>} />
        <Route path="/organiser/clubs" element={<Protected roles={["league_organiser", "super_admin"]}><OrganiserClubs /></Protected>} />

        <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminStats /></Protected>} />
        <Route path="/admin/clubs" element={<Protected roles={["super_admin"]}><AdminClubs /></Protected>} />
        <Route path="/admin/invites" element={<Protected roles={["super_admin"]}><AdminInvites /></Protected>} />
        <Route path="/admin/feedback" element={<Protected roles={["super_admin"]}><AdminFeedback /></Protected>} />
        <Route path="/admin/settings" element={<Protected roles={["super_admin"]}><AdminSettings /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={["super_admin"]}><AdminUsers /></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {showTabs && <BottomTabs />}
    </>
  );
}

export default function App() {
  return (
    <div className="vollea-app">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
